import { Injectable } from "@angular/core";
import { CacheService } from "@app-cfs/shared/services/cache.service";
import { CFSConnectionSettings } from "cfs-communication-pack";
import { ApplicationStyles, DefaultTemplateType, QrCodeConfiguration } from "clearline-api";
import { Template } from "clearline-common";
import { LocalStorageService } from "../../core/services";
import { StorageKeys } from "../models/storage-keys";

const fallbackQrCodeConfiguration: QrCodeConfiguration = {
  primaryColor: "#000000",
  secondaryColor: "#FFFFFF",
  backgroundColor: "#FFFFFF",
  primaryTextColor: "#000000",
  secondaryTextColor: "#FFFFFF",
  logo: "",
  qrLogoType: 0,
  hasFrame: false
};

@Injectable({
  providedIn: "root"
})
export class SettingsHandlerService {
  private explicitCashRegisterId = 0;
  private explicitLocationId = 0;
  private applicationStyles: ApplicationStyles | null = null;

  constructor(private localStorageService: LocalStorageService, private cacheService: CacheService) {}

  get templateList(): Template[] {
    return this.getTemplateListFromStorage();
  }

  get locationId(): number {
    return this.explicitLocationId || this.getLocationIdFromStorage();
  }

  get cashRegister(): number {
    return this.explicitCashRegisterId || this.getCashRegisterFromStorage();
  }

  get qrCodeConfiguration(): QrCodeConfiguration {
    return !!this.applicationStyles ? this.applicationStyles.qrConfiguration : fallbackQrCodeConfiguration;
  }

  setApplicationStyles(styles: ApplicationStyles): void {
    this.applicationStyles = styles;
  }

  startReset(): void {
    const code = this.localStorageService.getItem(StorageKeys.connectionCode);

    this.cacheService.clear();
    this.setResetInProgress();

    if (code) {
      this.localStorageService.setItem(StorageKeys.connectionCode, code);
    }
  }

  finishReset(): void {
    this.localStorageService.clearItem(StorageKeys.isResetInProgress);
  }

  setExplicitCashRegisterId(id: number): void {
    this.explicitCashRegisterId = id;
  }

  setExplicitLocationId(id: number): void {
    this.explicitLocationId = id;
  }

  saveTemplates(templates: Template[]): void {
    const filteredTemplates: Template[] = templates.filter((template: Template) => {
      return template.templateType === DefaultTemplateType.CfsAndPrint || template.templateType === DefaultTemplateType.Cfs;
    });

    this.setTemplateListToStorage(filteredTemplates);
  }

  saveSettings(settings: CFSConnectionSettings): void {
    const { locationId, cashRegisterId } = settings;

    this.saveCashRegister(cashRegisterId as number);
    this.saveLocation(locationId as number);
  }

  saveCashRegister(cashRegisterId: number): void {
    this.setCashRegisterToStorage(cashRegisterId);
  }

  saveLocation(locationId: number): void {
    this.setLocationToStorage(locationId);
  }

  setTemplateListToStorage(list: Template[]): void {
    this.localStorageService.setItem(StorageKeys.templateUrlListStorageKey, JSON.stringify(list));
  }

  isResetInProgress(): boolean {
    return this.localStorageService.getItem(StorageKeys.isResetInProgress) === "true";
  }

  clearAllStorage(): void {
    this.localStorageService.clearAll();
  }

  private getTemplateListFromStorage(): Template[] {
    try {
      return JSON.parse(this.localStorageService.getItem(StorageKeys.templateUrlListStorageKey));
    } catch (_) {
      console.warn("Failed to parse template list from storage. Returning empty list.");
      return [];
    }
  }

  private getLocationIdFromStorage(): number {
    const locationId = this.localStorageService.getItem(StorageKeys.locationStorageKey);

    return +locationId;
  }

  private getCashRegisterFromStorage(): number {
    return +this.localStorageService.getItem(StorageKeys.cashRegister);
  }

  private setCashRegisterToStorage(cashRegister: number): void {
    this.localStorageService.setItem(StorageKeys.cashRegister, cashRegister.toString());
  }

  private setLocationToStorage(location: number): void {
    this.localStorageService.setItem(StorageKeys.locationStorageKey, location.toString());
  }

  private setResetInProgress(): void {
    this.localStorageService.setItem(StorageKeys.isResetInProgress, "true");
  }
}
